import React from 'react';
import { PublicLayout } from '@/components/layout/PublicLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Helmet } from 'react-helmet-async';
import { Cookie, Shield, BarChart3, Settings, Globe, Clock, AlertTriangle, RefreshCw, Mail } from 'lucide-react';

export default function CookiesPage() {
  const effectiveDate = "January 12, 2026";

  return (
    <PublicLayout>
      <Helmet>
        <title>Cookie Policy | Kosha Tax Consultants</title>
        <meta name="description" content="Learn about how Kosha Tax Consultants uses cookies and similar technologies on our website." />
      </Helmet>

      <div className="bg-gradient-to-b from-blue-50 to-white dark:from-gray-900 dark:to-gray-800 py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-4xl font-bold text-center mb-4 text-foreground">Cookie Policy</h1>
            <p className="text-center text-muted-foreground mb-8">Last Updated: {effectiveDate}</p>
            
            <Card className="shadow-lg">
              <CardContent className="prose prose-lg dark:prose-invert max-w-none p-6 md:p-8">
                
                {/* Introduction */}
                <section className="mb-8">
                  <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 not-prose mb-4">
                    <p className="text-blue-800 dark:text-blue-200">
                      <strong>Kosha Tax Consultants</strong> is a trade name (DBA) of <strong>Annapurna Events LLC</strong>, 
                      a Texas limited liability company (&quot;Company,&quot; &quot;we,&quot; &quot;us,&quot; or &quot;our&quot;). 
                      This Cookie Policy explains how we use cookies and similar technologies on our website.
                    </p>
                  </div>
                </section>

                {/* What Are Cookies */}
                <section className="mb-8">
                  <div className="flex items-center gap-2 mb-4">
                    <Cookie className="h-5 w-5 text-primary flex-shrink-0" />
                    <h2 className="text-2xl font-semibold m-0">What Are Cookies?</h2>
                  </div>
                  <p>
                    Cookies are small text files placed on your computer or mobile device when you visit a website. 
                    Cookies are widely used to make websites function properly, improve efficiency, and provide 
                    information to website operators.
                  </p>
                  <p>
                    Cookies help us understand how users interact with our website, remember preferences, and 
                    improve overall user experience.
                  </p>
                </section>

                {/* How We Use Cookies */}
                <section className="mb-8">
                  <h2 className="text-2xl font-semibold">How We Use Cookies</h2>
                  <p>
                    We use cookies and similar technologies for the purposes described below.
                  </p>
                </section>

                {/* Essential Cookies */}
                <section className="mb-8">
                  <div className="flex items-center gap-2 mb-4">
                    <Shield className="h-5 w-5 text-primary flex-shrink-0" />
                    <h2 className="text-2xl font-semibold m-0">Essential Cookies (Strictly Necessary)</h2>
                  </div>
                  <p>
                    These cookies are required for the website to function and cannot be disabled in our systems. 
                    They support core features such as:
                  </p>
                  <ul>
                    <li>User authentication and session management</li>
                    <li>Security and fraud prevention</li>
                    <li>Form submission and data integrity</li>
                    <li>Load balancing and site availability</li>
                  </ul>
                  <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4 not-prose">
                    <p className="text-amber-800 dark:text-amber-200 text-sm">
                      <strong>Important:</strong> Without these cookies, the website may not function correctly.
                    </p>
                  </div>
                </section>

                {/* Performance and Analytics Cookies */}
                <section className="mb-8">
                  <div className="flex items-center gap-2 mb-4">
                    <BarChart3 className="h-5 w-5 text-primary flex-shrink-0" />
                    <h2 className="text-2xl font-semibold m-0">Performance and Analytics Cookies</h2>
                  </div>
                  <p>
                    These cookies help us understand how users interact with our website by collecting aggregated 
                    and anonymized information, such as:
                  </p>
                  <ul>
                    <li>Pages visited and time spent on the site</li>
                    <li>Website performance and error reporting</li>
                    <li>General navigation patterns</li>
                  </ul>
                  <p>
                    Analytics services may include tools such as Google Analytics or similar technologies. 
                    These cookies are used solely to improve website functionality and user experience.
                  </p>
                </section>

                {/* Functionality Cookies */}
                <section className="mb-8">
                  <div className="flex items-center gap-2 mb-4">
                    <Settings className="h-5 w-5 text-primary flex-shrink-0" />
                    <h2 className="text-2xl font-semibold m-0">Functionality Cookies</h2>
                  </div>
                  <p>
                    These cookies allow the website to remember choices you make, such as:
                  </p>
                  <ul>
                    <li>Login preferences</li>
                    <li>Language or regional settings</li>
                    <li>Accessibility preferences</li>
                  </ul>
                  <p>
                    They enhance functionality and personalization but are not strictly necessary for basic site operation.
                  </p>
                </section>

                {/* Third-Party Cookies */}
                <section className="mb-8">
                  <div className="flex items-center gap-2 mb-4">
                    <Globe className="h-5 w-5 text-primary flex-shrink-0" />
                    <h2 className="text-2xl font-semibold m-0">Third-Party Cookies</h2>
                  </div>
                  <p>
                    Some cookies may be placed by third-party service providers that support our operations. 
                    These may include, but are not limited to:
                  </p>
                  <ul>
                    <li>Payment processors (such as Stripe) for secure transaction processing</li>
                    <li>Analytics providers for website performance insights</li>
                    <li>Customer support or communication tools</li>
                  </ul>
                  <div className="bg-slate-100 dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-lg p-4 not-prose">
                    <p className="text-slate-700 dark:text-slate-300 text-sm">
                      Third-party cookies are governed by the privacy policies of the respective providers. 
                      We do not control how third parties use their cookies.
                    </p>
                  </div>
                </section>

                {/* Cookie Choices and Controls */}
                <section className="mb-8">
                  <h2 className="text-2xl font-semibold">Cookie Choices and Controls</h2>
                  <p>
                    You can manage cookie preferences through your browser settings. Most browsers allow you to:
                  </p>
                  <ul>
                    <li>View stored cookies</li>
                    <li>Delete cookies</li>
                    <li>Block or restrict cookies</li>
                    <li>Configure preferences for specific websites</li>
                  </ul>
                  <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4 not-prose">
                    <p className="text-amber-800 dark:text-amber-200 text-sm">
                      <strong>Note:</strong> Disabling cookies may impact website functionality, including account 
                      access and saved preferences.
                    </p>
                  </div>
                </section>

                {/* Cookie Retention */}
                <section className="mb-8">
                  <div className="flex items-center gap-2 mb-4">
                    <Clock className="h-5 w-5 text-primary flex-shrink-0" />
                    <h2 className="text-2xl font-semibold m-0">Cookie Retention</h2>
                  </div>
                  <ul>
                    <li><strong>Session cookies</strong> are deleted when you close your browser.</li>
                    <li><strong>Persistent cookies</strong> remain on your device for a defined period or until manually deleted.</li>
                  </ul>
                </section>

                {/* Impact of Disabling Cookies */}
                <section className="mb-8">
                  <div className="flex items-center gap-2 mb-4">
                    <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0" />
                    <h2 className="text-2xl font-semibold m-0">Impact of Disabling Cookies</h2>
                  </div>
                  <p>If cookies are disabled, some features may not function properly, including:</p>
                  <ul>
                    <li>Secure login</li>
                    <li>Form submissions</li>
                    <li>Preference retention</li>
                  </ul>
                </section>

                {/* Updates to This Policy */}
                <section className="mb-8">
                  <div className="flex items-center gap-2 mb-4">
                    <RefreshCw className="h-5 w-5 text-primary flex-shrink-0" />
                    <h2 className="text-2xl font-semibold m-0">Updates to This Policy</h2>
                  </div>
                  <p>
                    We may update this Cookie Policy from time to time to reflect changes in technology, legal 
                    requirements, or our data practices. Updates will be posted on this page with a revised 
                    &quot;Last Updated&quot; date.
                  </p>
                </section>

                {/* Contact Information */}
                <section>
                  <div className="flex items-center gap-2 mb-4">
                    <Mail className="h-5 w-5 text-primary flex-shrink-0" />
                    <h2 className="text-2xl font-semibold m-0">Contact Us</h2>
                  </div>
                  <p>For questions about this Cookie Policy or our data practices, contact:</p>
                  <div className="bg-muted/50 p-4 rounded-lg not-prose">
                    <p className="font-medium">Kosha Tax Consultants</p>
                    <p className="text-muted-foreground">A DBA of Annapurna Events LLC (Texas)</p>
                    <p className="text-muted-foreground">Email: help@koshatax.com</p>
                  </div>
                </section>

              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </PublicLayout>
  );
}
